import { useMemo } from "react";
import { F, D } from "../../../styles/palette";
import { DC, Bdg } from "../../shared";
import LineChart from "../../shared/LineChart";
import useClinicalRecords from "../../../hooks/useClinicalRecords";
import { ISL } from "../../../data/islStages";

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ flex: 1, minWidth: 120, background: "rgba(15,23,42,0.6)", border: `1px solid ${D.bdr}`, borderRadius: 12, padding: "14px 16px", textAlign: "center" }}>
      <div style={{ fontSize: 22, color: color || D.ac, fontFamily: F.m, fontWeight: 700 }}>{value ?? "—"}</div>
      <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: F.b, marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: "#64748b", fontFamily: F.b, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function PatientDashboard({ patientId }) {
  const { records, loading } = useClinicalRecords(patientId);

  // Datos para cada gráfica (orden cronológico asc)
  const sorted = useMemo(() => {
    if (!records) return [];
    return [...records].sort((a, b) => {
      const ta = a.fecha?.toDate ? a.fecha.toDate().getTime() : 0;
      const tb = b.fecha?.toDate ? b.fecha.toDate().getTime() : 0;
      return ta - tb;
    });
  }, [records]);

  const volData = useMemo(() =>
    sorted
      .filter(r => r.objetivo?.volResultados?.diffPct != null)
      .map(r => ({ x: r.fecha, y: parseFloat(r.objetivo.volResultados.diffPct.toFixed(1)) })),
    [sorted]);

  const vasData = useMemo(() =>
    sorted
      .filter(r => r.objetivo?.vasDolor != null)
      .map(r => ({ x: r.fecha, y: r.objetivo.vasDolor })),
    [sorted]);

  const islData = useMemo(() =>
    sorted
      .filter(r => r.objetivo?.estadioISL != null)
      .map(r => ({ x: r.fecha, y: r.objetivo.estadioISL, isl: r.objetivo.estadioISL })),
    [sorted]);

  // Estadísticas de resumen
  const totalSesiones = sorted.length;
  const ultimaVol = volData.length > 0 ? volData[volData.length - 1].y : null;
  const primeraVol = volData.length > 1 ? volData[0].y : null;
  const tendenciaVol = primeraVol != null && ultimaVol != null
    ? ultimaVol - primeraVol
    : null;
  const ultimoISL = islData.length > 0 ? islData[islData.length - 1].isl : null;
  const ultimoVAS = vasData.length > 0 ? vasData[vasData.length - 1].y : null;

  if (loading) {
    return <p style={{ color: D.txM, fontSize: 13, textAlign: "center", padding: 24 }}>Cargando datos...</p>;
  }

  if (totalSesiones === 0) {
    return (
      <DC title="Dashboard Clínico" accent={D.ac}>
        <p style={{ color: D.txM, fontSize: 13, textAlign: "center", padding: 16 }}>
          No hay evoluciones registradas para este paciente.
        </p>
      </DC>
    );
  }

  const islInfo = ultimoISL != null ? ISL[ultimoISL] : null;
  const tendenciaLabel = tendenciaVol != null
    ? (tendenciaVol < -5 ? "↓ Mejorando" : tendenciaVol > 5 ? "↑ Empeorando" : "→ Estable")
    : null;
  const tendenciaColor = tendenciaVol != null
    ? (tendenciaVol < -5 ? "#16a34a" : tendenciaVol > 5 ? "#dc2626" : "#ca8a04")
    : D.txM;

  return (
    <div>
      {/* Resumen estadístico */}
      <DC title="Resumen de Seguimiento" accent={D.ac}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <StatCard label="Sesiones totales" value={totalSesiones} />
          {ultimaVol != null && (
            <StatCard
              label="Últ. dif. volumétrica"
              value={`${ultimaVol > 0 ? "+" : ""}${ultimaVol}%`}
              sub={ultimaVol >= 20 ? "⚠ Umbral clínico" : "Dentro del rango"}
              color={ultimaVol >= 20 ? "#dc2626" : ultimaVol >= 10 ? "#ca8a04" : "#16a34a"}
            />
          )}
          {tendenciaLabel && (
            <StatCard label="Tendencia volumétrica" value={tendenciaLabel} color={tendenciaColor} />
          )}
          {ultimoVAS != null && (
            <StatCard
              label="Últ. VAS dolor"
              value={`${ultimoVAS}/10`}
              color={ultimoVAS > 6 ? "#dc2626" : ultimoVAS > 3 ? "#ca8a04" : "#16a34a"}
            />
          )}
          {islInfo && (
            <StatCard label="Estadio ISL actual" value={islInfo.s} sub={islInfo.n} color={islInfo.c} />
          )}
        </div>
      </DC>

      {/* Gráfica: Diferencia Volumétrica */}
      {volData.length > 0 && (
        <DC title="Diferencia Volumétrica (%)" accent="#2dd4bf">
          <div style={{ fontSize: 11, color: "#64748b", fontFamily: F.b, marginBottom: 8 }}>
            Diferencia porcentual entre miembro afectado y contralateral. Línea roja: umbral clínico ≥20%.
          </div>
          <LineChart
            data={volData}
            color="#2dd4bf"
            unit="%"
            yMin={0}
            refLine={20}
            refColor="#ef4444"
            refLabel="20%"
          />
        </DC>
      )}

      {/* Gráfica: VAS Dolor */}
      {vasData.length > 0 && (
        <DC title="VAS Dolor (0–10)" accent="#f97316">
          <div style={{ fontSize: 11, color: "#64748b", fontFamily: F.b, marginBottom: 8 }}>
            Escala visual análoga de dolor reportado por el paciente. Línea naranja: dolor moderado ≥4.
          </div>
          <LineChart
            data={vasData}
            color="#f97316"
            unit=" pts"
            yMin={0}
            yMax={10}
            refLine={4}
            refColor="#f97316"
            refLabel="4"
          />
        </DC>
      )}

      {/* Estadio ISL — dot timeline */}
      {islData.length > 0 && (
        <DC title="Evolución Estadio ISL" accent="#8b5cf6">
          <div style={{ fontSize: 11, color: "#64748b", fontFamily: F.b, marginBottom: 10 }}>
            Clasificación ISL registrada en cada sesión.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {islData.map((pt, i) => {
              const info = ISL[pt.isl];
              const dateObj = pt.x?.toDate ? pt.x.toDate() : (pt.x instanceof Date ? pt.x : null);
              const dateStr = dateObj ? dateObj.toLocaleDateString("es-CO", { day: "numeric", month: "short", year: "numeric" }) : "";
              return (
                <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: info.c, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontFamily: F.m, fontWeight: 700, color: "#fff" }}>
                    {pt.isl}
                  </div>
                  <span style={{ fontSize: 10, color: "#94a3b8", fontFamily: F.b, textAlign: "center", maxWidth: 60 }}>{dateStr}</span>
                  <Bdg t={info.n} c={info.c} />
                </div>
              );
            })}
          </div>
          {/* Leyenda */}
          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            {ISL.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: s.c }} />
                <span style={{ fontSize: 10, color: "#94a3b8", fontFamily: F.b }}>{s.s}: {s.n}</span>
              </div>
            ))}
          </div>
        </DC>
      )}

      {/* Mensaje si no hay suficientes datos en alguna sección */}
      {volData.length === 0 && vasData.length === 0 && (
        <DC title="Datos insuficientes" accent="#64748b">
          <p style={{ color: D.txM, fontSize: 12, margin: 0 }}>
            Las evoluciones aún no tienen datos de circometría o VAS registrados. Registra al menos una evolución con estos datos para ver las gráficas.
          </p>
        </DC>
      )}
    </div>
  );
}
